/* eslint-disable @typescript-eslint/naming-convention */
import { DownloadIcon, InfoOutlineIcon } from "@chakra-ui/icons"
import {
  Box,
  Button,
  Flex,
  Text,
  Tooltip,
  useBoolean,
  useMediaQuery
} from "@chakra-ui/react"
import React, { useState } from "react"
import { mobileBreakpoint } from "../../common/commonConstants"
import { buildDocumentUrl } from "../../common/documentUrl"
import { downloadPdf } from "../../common/downloadUtils"
import { useReportError, verifyPdfAccessible } from "../../common/commonHooks/useReportError"
import { Colors } from "../../theme/colors"
import { useGetReportLazyQuery, type ProductService } from "../../type-and-hooks"
import { ChevronDownIconComponent, ChevronRightIconComponent, ExpandableTableComponent } from "../commons/expandableTableComponent"
import { SnapPopoverNotes } from "../commons/popover"
import { PDFIcon } from "./product-info.main"

interface ReportGroupInterface {
  report_name: string
  report_url: string
  is_public?: boolean | null
}

const RESTRICTED_REPORT_MESSAGE = "* Please contact our customer support for the report."

interface TableReportsInterface {
  reportGroup: ReportGroupInterface[]
  isMobile: boolean
  serviceFRL: string
}

const TableReports = ({ reportGroup, isMobile, serviceFRL }: TableReportsInterface): React.ReactNode => {
  const [getReport] = useGetReportLazyQuery({ fetchPolicy: 'network-only' })
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const { reportError } = useReportError()

  const handleDirectDownload = async (report_name: string): Promise<void> => {
    setActionLoading(`dl-${report_name}`)
    try {
      const { data } = await getReport({ variables: { reportNo: report_name } })
      const url = data?.getReport?.downloadURL ?? data?.getReport?.reportURL ?? null
      if (!url) throw new Error("Report URL not found")
      const accessible = await verifyPdfAccessible(data?.getReport?.reportURL ?? url)
      if (!accessible) {
        reportError({ context: "Inaccessible Report", detail: `Report "${report_name}" returned access denied on product documentation tab` })
        return
      }
      window.open(url, "_blank", "noopener,noreferrer")
    } catch {
      reportError({ context: "Missing Report", detail: `Report "${report_name}" failed to download on product documentation tab` })
    } finally {
      setActionLoading(null)
    }
  }

  const handleReportClick = async (report_name: string): Promise<void> => {
    setActionLoading(report_name)
    try {
      const { data } = await getReport({ variables: { reportNo: report_name } })
      const url = data?.getReport?.reportURL ?? null
      if (!url) throw new Error("Report URL not found")
      const accessible = await verifyPdfAccessible(url)
      if (!accessible) {
        reportError({ context: "Inaccessible Report", detail: `Report "${report_name}" returned access denied on product documentation tab` })
        return
      }
      window.open(buildDocumentUrl(data?.getReport?.fileName, url) ?? url, "_blank", "noopener,noreferrer")
    } catch {
      reportError({ context: "Missing Report", detail: `Report "${report_name}" failed to load on product documentation tab` })
    } finally {
      setActionLoading(null)
    }
  }

  return (
    <>
      {!isMobile &&
        reportGroup.map(({ report_name, is_public }) => (
          <Flex key={report_name} align="center" gap="6px">
            {is_public === false ? (
              <>
                <Box p="0" minW="0" h="auto" mr="8px" visibility="hidden" aria-hidden="true">
                  <DownloadIcon />
                </Box>
                <Tooltip label={RESTRICTED_REPORT_MESSAGE} hasArrow placement="top">
                  <Flex align="center" gap="8px" cursor="default">
                    <PDFIcon />
                    <Text as="span" color="black" fontWeight="400" fontSize="16px" lineHeight="24px">
                      {report_name} *
                    </Text>
                  </Flex>
                </Tooltip>
              </>
            ) : (
              <>
                <Button
                  variant="linkButton"
                  p="0"
                  minW="0"
                  h="auto"
                  mr="8px"
                  display="flex"
                  alignItems="center"
                  flexShrink={0}
                  onClick={() => { void handleDirectDownload(report_name) }}
                  isLoading={actionLoading === `dl-${report_name}`}
                  aria-label={`Download ${report_name}`}
                >
                  <DownloadIcon />
                </Button>
                <Button
                  variant="linkButton"
                  textDecoration="none"
                  fontWeight="400"
                  fontSize="16px"
                  lineHeight="24px"
                  h="auto"
                  display="flex"
                  alignItems="center"
                  gap="8px"
                  onClick={() => { void handleReportClick(report_name) }}
                  isLoading={actionLoading === report_name}
                >
                  <PDFIcon />
                  {report_name}
                </Button>
              </>
            )}
          </Flex>
        ))}
      {isMobile &&
        reportGroup.map(({ report_name, is_public }) => (
          <React.Fragment key={report_name}>
            <span style={{ fontFamily: "PT Sans", fontSize: "12px", fontStyle: "normal", fontWeight: 400, lineHeight: "16px" }}>{serviceFRL}</span>
            <Flex align="center" gap="6px">
              {is_public === false ? (
                <>
                  <Box p="0" minW="0" h="auto" mr="8px" visibility="hidden" aria-hidden="true">
                    <DownloadIcon fontSize="12px" />
                  </Box>
                  <Tooltip label={RESTRICTED_REPORT_MESSAGE} hasArrow placement="top">
                    <Flex align="center" gap="6px" cursor="default">
                      <PDFIcon />
                      <Text as="span" color="black" fontWeight="400" fontSize="12px" lineHeight="16px">
                        {report_name} *
                      </Text>
                    </Flex>
                  </Tooltip>
                </>
              ) : (
                <>
                  <Button
                    variant="linkButton"
                    p="0"
                    minW="0"
                    h="auto"
                    mr="8px"
                    display="flex"
                    alignItems="center"
                    flexShrink={0}
                    onClick={() => { void handleDirectDownload(report_name) }}
                    isLoading={actionLoading === `dl-${report_name}`}
                    aria-label={`Download ${report_name}`}
                  >
                    <DownloadIcon fontSize="12px" />
                  </Button>
                  <Button
                    variant="linkButton"
                    fontWeight="400"
                    fontSize="12px"
                    lineHeight="16px"
                    h="auto"
                    display="flex"
                    alignItems="center"
                    gap="6px"
                    onClick={() => { void handleReportClick(report_name) }}
                    isLoading={actionLoading === report_name}
                  >
                    <PDFIcon />
                    {report_name}
                  </Button>
                </>
              )}
            </Flex>
          </React.Fragment>
        ))}
    </>
  )
}

const objectToArray = (data: ProductService[], isDesktop: boolean): string[][] => {
  return data.reduce((acc: any[], curr: any) => {
    if (isDesktop) {
      acc.push([
        curr.service_name,
        curr.service_size,
        curr.system_frl,
        <SnapPopoverNotes
          key={`${curr.service_name}_${curr.service_size}_notes`}
          noteIcon={<InfoOutlineIcon />}
          text={curr.service_notes[0]}
        />,
        <TableReports
          isMobile={false}
          key={`${curr.service_name}_${curr.service_size}_reports`}
          reportGroup={curr.rep_grp}
          serviceFRL={curr.system_frl}
        />
      ])
    } else {
      acc.push([
        curr.service_name,
        curr.service_size,
        <SnapPopoverNotes
          key={`${curr.service_name}_${curr.service_size}_notes`}
          noteIcon={<InfoOutlineIcon />}
          text={curr.service_notes[0]}
        />,
        <TableReports
          isMobile={true}
          key={`${curr.service_name}_${curr.service_size}_reports`}
          reportGroup={curr.rep_grp}
          serviceFRL={curr.system_frl}
        />
      ])
    }
    return acc
  }, [])
}

interface MobileCardSectionProps {
  data: ProductService[]
  title: string
  applicationPrefix?: string | null
  isExpanded?: boolean
  onToggle?: () => void
}

const LABEL_STYLE = {
  fontSize: "11px",
  fontWeight: 600,
  textTransform: "uppercase" as const,
  color: "#939598",
  letterSpacing: "0.5px",
  flexShrink: 0
}

const VALUE_STYLE = {
  fontSize: "13px",
  color: "#231F20",
  textAlign: "left" as const
}

const MobileCardSection = ({ data, title, applicationPrefix, isExpanded: controlledExpand, onToggle }: MobileCardSectionProps): JSX.Element => {
  const [internalExpand, setInternalExpand] = useBoolean(false)
  const expand = controlledExpand !== undefined ? controlledExpand : internalExpand
  const handleToggle = (): void => {
    if (onToggle !== undefined) { onToggle() } else { setInternalExpand.toggle() }
  }
  const [getReport] = useGetReportLazyQuery({ fetchPolicy: 'network-only' })
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const { reportError } = useReportError()

  const handleDirectDownload = async (report_name: string): Promise<void> => {
    setActionLoading(`dl-${report_name}`)
    try {
      const { data } = await getReport({ variables: { reportNo: report_name } })
      const downloadUrl = data?.getReport?.downloadURL ?? data?.getReport?.reportURL ?? null
      if (!downloadUrl) throw new Error("Report URL not found")
      window.open(downloadUrl, "_blank", "noopener,noreferrer")
    } catch {
      reportError({ context: "Missing Report", detail: `Report "${report_name}" failed to download on mobile product documentation tab` })
    } finally {
      setActionLoading(null)
    }
  }

  const handleReportClick = async (report_name: string): Promise<void> => {
    setActionLoading(report_name)
    try {
      const { data } = await getReport({ variables: { reportNo: report_name } })
      const url = data?.getReport?.reportURL ?? null
      if (!url) throw new Error("Report URL not found")
      const accessible = await verifyPdfAccessible(url)
      if (!accessible) {
        reportError({ context: "Inaccessible Report", detail: `Report "${report_name}" returned access denied on mobile product documentation tab` })
        return
      }
      window.open(buildDocumentUrl(data?.getReport?.fileName, url) ?? url, "_blank", "noopener,noreferrer")
    } catch {
      reportError({ context: "Missing Report", detail: `Report "${report_name}" failed to load on mobile product documentation tab` })
    } finally {
      setActionLoading(null)
    }
  }

  return (
    <Box mt="1px">
      <Flex
        bg={Colors.Secondary.c2_500}
        px="5px"
        py="2"
        onClick={handleToggle}
        userSelect="none"
        align="center"
        cursor="pointer"
      >
        {expand ? <ChevronDownIconComponent /> : <ChevronRightIconComponent />}
        <Text variant="RCaption">{title?.split("|")[0] ?? ""}</Text>
      </Flex>

      <Box overflow={expand ? "visible" : "hidden"} maxHeight={expand ? "none" : "0"} transition="0.5s">
        {data?.map((service, i) => {
          const notes = (Array.isArray(service.service_notes?.[0])
            ? (service.service_notes![0] as string[]).filter(Boolean)
            : []) as string[]
          const reports = service.rep_grp?.filter(Boolean) ?? []

          return (
            <Box
              key={i}
              borderBottom={`1px solid ${Colors.Secondary.c2_300}`}
              px="4"
              py="3"
            >
              <Flex justify="flex-start" align="baseline" mb="6px" gap="3">
                <Text style={LABEL_STYLE}>Material</Text>
                <Text style={VALUE_STYLE}>
                  {applicationPrefix ? `${applicationPrefix} ` : ""}
                  {service.service_name ?? "—"}
                </Text>
              </Flex>

              <Flex justify="flex-start" align="baseline" mb="6px" gap="3">
                <Text style={LABEL_STYLE}>Size</Text>
                <Text style={VALUE_STYLE}>{service.service_size ?? "—"}</Text>
              </Flex>

              <Flex justify="flex-start" align="baseline" mb="6px" gap="3">
                <Text style={LABEL_STYLE}>FRL</Text>
                <Text style={VALUE_STYLE}>{service.system_frl ?? "—"}</Text>
              </Flex>

              <Flex justify="flex-start" align="center" mb="8px" gap="3">
                <Text style={LABEL_STYLE}>Note</Text>
                <Text style={VALUE_STYLE}>{notes.length > 0 ? notes[0] : "—"}</Text>
              </Flex>

              <Box borderTop={`1px solid ${Colors.Secondary.c2_300}`} my="8px" />

              <Flex justify="flex-start" align="center" gap="3">
                <Text style={LABEL_STYLE}>Report</Text>
                <Box display="flex" flexDirection="column" alignItems="flex-start" gap="4px">
                  {reports.length > 0 ? (
                    reports.map(report => (
                      <Flex key={report!.report_name} align="center" gap="6px">
                        {report!.is_public === false ? (
                          <>
                            <Box p="0" minW="0" h="auto" mr="8px" visibility="hidden" aria-hidden="true">
                              <DownloadIcon fontSize="12px" />
                            </Box>
                            <Tooltip label={RESTRICTED_REPORT_MESSAGE} hasArrow placement="top">
                              <Flex align="center" gap="6px" cursor="default">
                                <PDFIcon />
                                <Text as="span" color="black" fontWeight="400" fontSize="12px" lineHeight="16px">
                                  {report!.report_name} *
                                </Text>
                              </Flex>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <Button
                              variant="linkButton"
                              p="0"
                              minW="0"
                              h="auto"
                              mr="8px"
                              display="flex"
                              alignItems="center"
                              flexShrink={0}
                              onClick={() => { void handleDirectDownload(report!.report_name) }}
                              isLoading={actionLoading === `dl-${report!.report_name}`}
                              aria-label={`Download ${report!.report_name}`}
                            >
                              <DownloadIcon fontSize="12px" />
                            </Button>
                            <Button
                              variant="linkButton"
                              fontWeight="400"
                              fontSize="12px"
                              lineHeight="16px"
                              h="auto"
                              color="#861F41"
                              display="flex"
                              alignItems="center"
                              gap="6px"
                              p="0"
                              minW="0"
                              onClick={() => { void handleReportClick(report!.report_name) }}
                              isLoading={actionLoading === report!.report_name}
                            >
                              <PDFIcon />
                              {report!.report_name}
                            </Button>
                          </>
                        )}
                      </Flex>
                    ))
                  ) : (
                    <Text style={VALUE_STYLE}>—</Text>
                  )}
                </Box>
              </Flex>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

interface ProductTableContentInterface {
  data: ProductService[]
  hideHeading: boolean
  title: string
  headings: string[]
  applicationPrefix?: string | null
  isExpanded?: boolean
  onToggle?: () => void
}

export const ProductTableContent = ({ data, title, headings, applicationPrefix = "", hideHeading, isExpanded, onToggle }: ProductTableContentInterface): JSX.Element => {
  const [isDesktop] = useMediaQuery(`(min-width: ${mobileBreakpoint}px)`)

  return (
    <>
      {/* Desktop table — hidden below 991px via CSS media query */}
      <Box sx={{ display: "none", [`@media (min-width: ${mobileBreakpoint}px)`]: { display: "block" } }}>
        <ExpandableTableComponent
          data={{
            headings,
            hideHeading,
            expandableTitle: title,
            tableData: objectToArray(data, isDesktop),
            applicationPrefix
          }}
          isExpanded={isExpanded}
          onToggle={onToggle}
        />
      </Box>

      {/* Mobile card layout — hidden at 991px+ via CSS media query */}
      <Box sx={{ display: "block", [`@media (min-width: ${mobileBreakpoint}px)`]: { display: "none" } }}>
        <MobileCardSection
          data={data}
          title={title}
          applicationPrefix={applicationPrefix}
          isExpanded={isExpanded}
          onToggle={onToggle}
        />
      </Box>
    </>
  )
}
